// src/pages/admin/MonthlyVisits.tsx
import React, { useEffect, useState } from "react";
import { FaChartLine, FaDownload } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

type MonthlyVisitData = {
  courseId: number;
  courseTitle: string;
  year: number;
  month: number;
  visitCount: number;
};

const MonthlyVisits: React.FC = () => {
  const [visitData, setVisitData] = useState<
    Record<string, Record<string, number>>
  >({});
  const [months, setMonths] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchMonthlyVisits();
  }, []);

  const fetchMonthlyVisits = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/admin/visits/monthly-table`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch monthly visits");
      }

      const data = await response.json();
      setVisitData(data);

      // Extract unique months from all courses
      const allMonths = new Set<string>();
      Object.values(data).forEach((courseData: any) => {
        Object.keys(courseData).forEach((month) => allMonths.add(month));
      });
      setMonths(Array.from(allMonths).sort().reverse());
      setLoading(false);
    } catch (err) {
      console.error("Error fetching visit data:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
      setLoading(false);
    }
  };

  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const getTotalForMonth = (month: string) => {
    return Object.values(visitData).reduce((sum, courseData) => {
      return sum + (courseData[month] || 0);
    }, 0);
  };

  const getTotalForCourse = (monthData: Record<string, number>) => {
    return Object.values(monthData).reduce((sum, count) => sum + count, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaChartLine />}
          title="Monthly Course Visits"
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Dashboard", to: "/admin/dashboard" },
            { label: "Monthly Visits" },
          ]}
        />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading visit statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaChartLine />}
          title="Monthly Course Visits"
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Dashboard", to: "/admin/dashboard" },
            { label: "Monthly Visits" },
          ]}
        />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchMonthlyVisits}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaChartLine />}
        title="Monthly Course Visits"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Monthly Visits" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#009fe3]">
            <div className="text-gray-600 text-sm mb-2">Total Courses</div>
            <div className="text-3xl font-bold text-gray-800">
              {Object.keys(visitData).length}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="text-gray-600 text-sm mb-2">
              This Month ({months[0] ? formatMonth(months[0]) : "-"})
            </div>
            <div className="text-3xl font-bold text-gray-800">
              {months[0] ? getTotalForMonth(months[0]).toLocaleString() : 0}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="text-gray-600 text-sm mb-2">Average per Course</div>
            <div className="text-3xl font-bold text-gray-800">
              {months[0] && Object.keys(visitData).length > 0
                ? Math.round(
                    getTotalForMonth(months[0]) / Object.keys(visitData).length,
                  )
                : 0}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white">
                  <th className="px-6 py-4 text-left font-semibold sticky left-0 bg-[#009fe3] z-10">
                    Course Name
                  </th>
                  {months.map((month) => (
                    <th
                      key={month}
                      className="px-6 py-4 text-center font-semibold whitespace-nowrap"
                    >
                      {formatMonth(month)}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-center font-semibold bg-[#0088cc]">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Object.entries(visitData).map(
                  ([courseName, monthData], index) => (
                    <tr
                      key={courseName}
                      className={`hover:bg-blue-50 transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      <td className="px-6 py-4 font-medium text-gray-900 sticky left-0 bg-inherit z-10">
                        {courseName}
                      </td>
                      {months.map((month) => (
                        <td key={month} className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium ${
                              monthData[month] > 200
                                ? "bg-green-100 text-green-800"
                                : monthData[month] > 100
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {monthData[month] || 0}
                          </span>
                        </td>
                      ))}
                      <td className="px-6 py-4 text-center font-bold text-gray-900 bg-gray-100">
                        {getTotalForCourse(monthData).toLocaleString()}
                      </td>
                    </tr>
                  ),
                )}
                {/* Total Row */}
                <tr className="bg-gradient-to-r from-gray-100 to-gray-200 font-bold">
                  <td className="px-6 py-4 text-gray-900 sticky left-0 bg-gray-100 z-10">
                    📈 Total Visits
                  </td>
                  {months.map((month) => (
                    <td
                      key={month}
                      className="px-6 py-4 text-center text-gray-900"
                    >
                      {getTotalForMonth(month).toLocaleString()}
                    </td>
                  ))}
                  <td className="px-6 py-4 text-center text-gray-900 bg-gray-200">
                    {Object.values(visitData)
                      .reduce((total, courseData) => {
                        return total + getTotalForCourse(courseData);
                      }, 0)
                      .toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MonthlyVisits;
